import { CdnAsset } from "#components/ui/CdnAsset.tsx";
import { useFileMetadata } from "#features/conversations/hooks/useFileMetadata.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { GeneratedFile } from "#types/types.ts";
import { Div, Flex, LinkButton, Text } from "@hopper-ui/components";
import { DownloadIcon } from "@hopper-ui/icons";
import { useEffect, useState } from "react";

interface Props {
    file: GeneratedFile;
}

const BYTE_UNITS = ["B", "KB", "MB", "GB"] as const;
const MAX_TIMEOUT_MS = 2_147_483_647;

export const formatFileSize = (sizeBytes: number, locale: string): string => {
    if (!Number.isFinite(sizeBytes) || sizeBytes <= 0) {
        return `0 ${BYTE_UNITS[0]}`;
    }

    const unitIndex = Math.min(Math.floor(Math.log(sizeBytes) / Math.log(1024)), BYTE_UNITS.length - 1);
    const value = sizeBytes / 1024 ** unitIndex;
    const formattedValue = new Intl.NumberFormat(locale, {
        maximumFractionDigits: unitIndex === 0 ? 0 : 1
    }).format(value);

    return `${formattedValue} ${BYTE_UNITS[unitIndex]}`;
};

export const isGeneratedFileExpired = (expiresAt: string, now = Date.now()): boolean => {
    const expiresAtTime = Date.parse(expiresAt);

    return Number.isFinite(expiresAtTime) && expiresAtTime <= now;
};

const useIsGeneratedFileExpired = (expiresAt: string): boolean => {
    const [expiredAt, setExpiredAt] = useState<string | null>(
        isGeneratedFileExpired(expiresAt) ? expiresAt : null
    );

    useEffect(() => {
        let timeoutId: number | undefined;

        const updateExpiryState = () => {
            const expiresAtTime = Date.parse(expiresAt);

            if (!Number.isFinite(expiresAtTime)) {
                setExpiredAt(null);

                return;
            }

            const delay = expiresAtTime - Date.now();

            if (delay <= 0) {
                setExpiredAt(expiresAt);

                return;
            }

            setExpiredAt(null);
            timeoutId = window.setTimeout(updateExpiryState, Math.min(delay, MAX_TIMEOUT_MS));
        };

        updateExpiryState();

        return () => {
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [expiresAt]);

    return expiredAt === expiresAt || isGeneratedFileExpired(expiresAt);
};

export const GeneratedFileCard = ({ file }: Props) => {
    const { tShared, i18n } = useSharedTranslation();
    const { getFileTypeIcon, getFileTypeName } = useFileMetadata();
    const isExpired = useIsGeneratedFileExpired(file.expiresAt);
    const fileTypeName = getFileTypeName(file.fileName, file.mediaType);
    const metadata = [fileTypeName, formatFileSize(file.sizeBytes, i18n.language)].join(" · ");

    return (
        <Flex
            alignItems="center"
            justifyContent="space-between"
            gap="inline-lg"
            padding="inset-md"
            border="neutral-weakest"
            borderRadius="rounded-lg"
            UNSAFE_maxWidth="34rem"
        >
            <Flex alignItems="center" gap="inline-sm" minWidth={0} grow={1}>
                <Flex
                    width="core_320"
                    height="core_320"
                    borderRadius="rounded-md"
                    justifyContent="center"
                    alignItems="center"
                    shrink={0}
                    backgroundColor="neutral-weakest"
                >
                    <CdnAsset
                        asset={getFileTypeIcon(file.fileName, file.mediaType)}
                        width="core_160"
                        height="core_160"
                        alt=""
                    />
                </Flex>
                <Flex direction="column" gap="inline-xs" minWidth={0}>
                    <Div UNSAFE_lineHeight={1}>
                        <Text
                            size="sm"
                            fontWeight="body-sm-medium"
                            color="neutral"
                            title={file.fileName}
                            className="wai-line-clamp"
                        >
                            {file.fileName}
                        </Text>
                    </Div>
                    <Div UNSAFE_lineHeight={1}>
                        <Text size="xs" color="neutral-weak">
                            {metadata}
                        </Text>
                    </Div>
                    {isExpired && (
                        <Div UNSAFE_lineHeight={1}>
                            <Text size="xs" color="danger-selected">
                                {tShared("generatedFileCard.expired")}
                            </Text>
                        </Div>
                    )}
                </Flex>
            </Flex>
            <LinkButton
                href={file.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                download={file.fileName}
                variant="ghost-secondary"
                size="sm"
                isDisabled={isExpired}
                flexShrink={0}
                marginLeft="auto"
                borderRadius="circle"
                backgroundColor="neutral-weakest"
                backgroundColorHover="neutral-weak"
                aria-label={tShared("generatedFileCard.downloadAriaLabel", {
                    fileName: file.fileName,
                    fileType: fileTypeName
                })}
            >
                <DownloadIcon />
            </LinkButton>
        </Flex>
    );
};
